<script lang="ts">
  import type { Product } from "$lib/types/products";
  import CallMeButton from "$lib/CallMeButton.svelte";
  import TypeformButton from "$lib/TypeformButton.svelte";
  import Button from "$lib/Button.svelte";
  import { browser } from "$app/environment";
  import { syncSessionValues } from "$lib/sessionValues";

  interface Props {
    product: Product;
    discountEligible: boolean;
    source: string;
    sticky?: boolean;
  }

  let { product, discountEligible, source, sticky = false }: Props = $props();

  let isVarmepumpe = $derived(product.product_categories.includes("varmepumpe"));
  let isElbillader = $derived(product.product_categories.includes("elbillader"));
  let isGavekort = $derived(product.product_categories.includes("gavekort"));
  let isElektriker = $derived(product.product_categories.includes("elektriker"));

  $effect(() => {
    if (browser) {
      syncSessionValues(
        {
          ...(!!(product?.typeform_name || product?.title) && {
            product: product?.typeform_name || product.title,
          }),
          ...(!!product?.typeform_id && { product_id: product.typeform_id }),
        },
        { useBrowserStorage: true }
      );
    }
  });
</script>

<section
  class:sticky
  class="footer bottom-[.5rem] laptop:bottom-[1rem] mx-.5edge-x px-.5edge-x desktop:mx-.75edge-x desktop:px-.25edge-x py-4 rounded-2xl laptop:rounded-full flex gap-y-2 gap-x-4 tablet:items-center flex-col tablet:flex-row"
>
  {#if !isGavekort}
    <h2 class="my-0 text-base font-400 hyphens-manual">
      <strong>{product.title} – {(product.price / (product.down_payment * 12)).toFixed(0)}</strong>
      kr i mnd
    </h2>
  {:else}
    <h2 class="my-0 text-base font-400 hyphens-manual">
      <strong>{product.title} – Beløp fra 100,-</strong>
    </h2>
  {/if}
  <div class="flex gap-2" class:buttons-col={isElektriker}>
    <CallMeButton --mx="0" class="laptop:hidden whitespace-nowrap" rotated={false}
      >Ring meg</CallMeButton
    >
    <p class="my-0" class:order-button={isElbillader || isElektriker || discountEligible}>
      {#if isVarmepumpe}
        <TypeformButton
          url="https://ishavskraft.typeform.com/to/m2jWCa5u"
          {source}
          class="button-primary-success"
          arrow
          additionalValuesKeys={["product", "product_id"]}
          customParameters={{
            ...(!!product?.campaign && { campaign: product.campaign }),
          }}
        >
          <span class="whitespace-nowrap">Bestill</span>{discountEligible ? " med bonus" : ""}
        </TypeformButton>
      {/if}
      {#if isGavekort}
        <TypeformButton
          url="https://ishavskraft.typeform.com/to/uD8f4Rry"
          {source}
          class="button-primary-success"
          arrow
          additionalValuesKeys={["product", "product_id"]}
          customParameters={{
            ...(!!product?.campaign && { campaign: product.campaign }),
          }}
        >
          <span class="whitespace-nowrap">Bestill</span>
        </TypeformButton>
      {/if}
      {#if isElbillader}
        <Button
          rel="nofollow"
          href={`/nettbutikk/${product.slug.current}/bestill?${new URLSearchParams({
            ...(!!(product?.typeform_name || product?.title) && {
              product: product?.typeform_name || product.title,
            }),
          }).toString()}`}
          arrow
          class="button-primary-success"
        >
          Finn nærmeste installatør
        </Button>
      {/if}
      {#if isElektriker}
        <Button
          rel="nofollow"
          href={`/nettbutikk/${product.slug.current}/bestill?${new URLSearchParams({
            ...(!!(product?.typeform_name || product?.title) && {
              product: product?.typeform_name || product.title,
            }),
          }).toString()}`}
          arrow
          class="button-primary-success"
        >
          Finn nærmeste elektriker
        </Button>
      {/if}
    </p>
  </div>
</section>

<style lang="postcss">
  .footer {
    background: linear-gradient(
      color-mix(in oklab, var(--skodde) 87.5%, transparent),
      color-mix(in oklab, var(--skodde) 75%, transparent)
    );
    backdrop-filter: blur(3px);
    box-shadow: 0 0 0 1px rgb(0 0 0 / 0.1);
    filter: drop-shadow(0 0 1rem rgb(0 0 0 / 0.2));
  }

  @media (max-width: 389px) {
    .buttons-col {
      flex-direction: column;
    }
    .buttons-col .order-button {
      margin-top: 0;
    }
    .order-button {
      flex: initial;
    }
  }
</style>
